/**
 * Profile Performance Details Page
 * Edit performance details: language, cast, duration, and other metadata
 * Uses performance details store and persists via artist API
 *
 * @author Varun Pandey
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Clock, Users, ArrowLeft, Plus, X, UserCircle, SlidersHorizontal, Languages, MapPin } from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { MultiSelect, MultiSelectOption } from '../../components/ui/MultiSelect';
import { Select, SelectOption } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { usePerformanceDetailsStore } from '@/stores/performanceDetailsStore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { Loader } from '@/components/ui';
import {
  ProfileCompletionBlocksControl,
  ProfileIncompleteCornerPing,
} from '@/components/profile/ProfileCompletionBlocksControl';
import {
  PERFORMING_LANGUAGE_OPTIONS,
  DEFAULT_PERFORMING_LANGUAGE_PRIMARY,
  canonicalPerformingLanguageSlug,
  isKnownPerformingLanguage,
} from '@/constants/performingLanguages';

const genderOptions: SelectOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'others', label: 'Others' },
];

function normalizeOpenToTravel(
  value: string | null | undefined,
): string {
  if (!value) return '';
  const v = String(value).trim().toLowerCase();
  if (v === 'nationwide') return 'nationwide';
  if (v === 'no') return 'no';
  return '';
}

interface MemberRow {
  id: string;
  name: string;
  role: string;
  age: string;
  gender: string;
}

export const ProfilePerformanceDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const data = usePerformanceDetailsStore((s) => s.data);
  const isLoading = usePerformanceDetailsStore((s) => s.isLoading);
  const saveLoading = usePerformanceDetailsStore((s) => s.saveLoading);
  const error = usePerformanceDetailsStore((s) => s.error);
  const ensureFetched = usePerformanceDetailsStore((s) => s.ensureFetched);
  const updatePerformanceDetails = usePerformanceDetailsStore(
    (s) => s.updatePerformanceDetails,
  );
  const deletePerformingMember = usePerformanceDetailsStore(
    (s) => s.deletePerformingMember,
  );
  const fetchPerformanceDetails = usePerformanceDetailsStore(
    (s) => s.fetchPerformanceDetails,
  );

  const incompletedSections = useArtistDetailsStore((s) => s.data?.incompleted_sections);
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);

  const performancePrefsNeedAttention = useMemo(() => {
    if (!Array.isArray(incompletedSections)) return false;
    const set = new Set(incompletedSections);
    return set.has('performing_languages') || set.has('performance_duration');
  }, [incompletedSections]);

  const [extraLanguageOptions, setExtraLanguageOptions] = useState<
    MultiSelectOption[]
  >([]);
  const [languages, setLanguages] = useState<string[]>([]);

  const languageOptions = useMemo(() => {
    const seen = new Set(PERFORMING_LANGUAGE_OPTIONS.map((o) => o.value));
    const extras = extraLanguageOptions.filter((o) => !seen.has(o.value));
    return [...PERFORMING_LANGUAGE_OPTIONS, ...extras];
  }, [extraLanguageOptions]);
  const [minDuration, setMinDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [openToTravel, setOpenToTravel] = useState('');
  const [offstageCrew, setOffstageCrew] = useState('');
  type MembersViewMode = 'view' | 'modify' | 'add';
  const [membersViewMode, setMembersViewMode] = useState<MembersViewMode>('view');
  const [draftNewMembers, setDraftNewMembers] = useState<MemberRow[]>([
    { id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' },
  ]);
  const [performanceErrors, setPerformanceErrors] = useState<Record<string, string>>({});
  const [memberErrors, setMemberErrors] = useState<Record<string, string>>({});
  const [performancePrefsEditing, setPerformancePrefsEditing] = useState(false);
  const performanceRefs = useRef<Record<string, HTMLInputElement | HTMLButtonElement | null>>({});
  const memberRefs = useRef<Record<string, HTMLInputElement | HTMLButtonElement | null>>({});
  const languagesSectionRef = useRef<HTMLDivElement>(null);

  // First time in session: fetch and update store; later visits use cached store data
  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  useEffect(() => {
    void ensureArtistDetailsFetched();
  }, [ensureArtistDetailsFetched]);

  // Keyboard handling for safe-area/padding is centralized in `BottomNav`/`UserHome`.

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // Sync store.data → local form state whenever store updates (initial load or after PUT)
  useEffect(() => {
    if (!data) return;

    const extras: MultiSelectOption[] = [];
    const seenExtra = new Set<string>();
    if (data.performing_language?.length) {
      const langs = data.performing_language.map((l) =>
        canonicalPerformingLanguageSlug(l.l_slug),
      );
      setLanguages(langs);
      for (const l of data.performing_language) {
        const canon = canonicalPerformingLanguageSlug(l.l_slug);
        if (!isKnownPerformingLanguage(canon)) {
          const v = canon;
          if (v && !seenExtra.has(v)) {
            seenExtra.add(v);
            extras.push({ value: v, label: l.language_name || v });
          }
        }
      }
    } else {
      setLanguages([]);
    }
    setExtraLanguageOptions(extras);

    if (data.min_duration_mins != null) {
      setMinDuration(String(data.min_duration_mins));
    }
    if (data.max_duration_mins != null) {
      setMaxDuration(String(data.max_duration_mins));
    }
    setOpenToTravel(
      normalizeOpenToTravel(data.open_to_travel as string | null | undefined),
    );
    // Sync offstage_crew from GET response: dropdown options are 1–20, so only set if in range
    const crewNum = data.offstage_crew != null ? Number(data.offstage_crew) : null;
    if (crewNum != null && crewNum >= 1 && crewNum <= 20) {
      setOffstageCrew(String(crewNum));
    } else {
      setOffstageCrew('');
    }
  }, [data]);

  const openToTravelValue =
    openToTravel ||
    normalizeOpenToTravel(data?.open_to_travel as string | null | undefined);

  const savedOpenToTravel = normalizeOpenToTravel(data?.open_to_travel as string | null | undefined);
  const savedLanguages = useMemo(
    () =>
      (data?.performing_language ?? [])
        .map((l) => canonicalPerformingLanguageSlug(l.l_slug))
        .sort()
        .join(','),
    [data?.performing_language],
  );
  const currentLanguagesKey = useMemo(() => [...languages].sort().join(','), [languages]);

  const hasPerformancePrefsChanged = useMemo(() => {
    if (!data) return false;
    if (savedLanguages !== currentLanguagesKey) return true;
    if (String(data.min_duration_mins ?? '') !== minDuration) return true;
    if (String(data.max_duration_mins ?? '') !== maxDuration) return true;
    if (savedOpenToTravel !== openToTravelValue) return true;
    if (String(data.offstage_crew ?? '') !== offstageCrew) return true;
    return false;
  }, [data, savedLanguages, currentLanguagesKey, minDuration, maxDuration, savedOpenToTravel, openToTravelValue, offstageCrew]);

  const handleAddMember = () => {
    setMembersViewMode('add');
    setDraftNewMembers([
      { id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' },
    ]);
    setMemberErrors({});
  };

  const handleAddMoreMembers = () => {
    setDraftNewMembers((prev) => [
      ...prev,
      { id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: '', role: '', age: '', gender: '' },
    ]);
  };

  const handleRemoveDraftMember = (id: string) => {
    setDraftNewMembers((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((m) => m.id !== id);
    });
  };

  const handleDeletePerformingMember = async (id: number) => {
    const list = data?.performing_members ?? [];
    if (list.length <= 1) {
      toast.error('At least one performing member is required.');
      return;
    }
    try {
      await deletePerformingMember(id);
      toast.success('Member removed');
    } catch {
      toast.error('Failed to delete member.');
    }
  };

  const handleSaveModify = async () => {
    await fetchPerformanceDetails();
    setMembersViewMode('view');
  };

  const handleCancelModify = () => {
    setMembersViewMode('view');
  };

  const handleDraftMemberChange = (id: string, field: keyof Omit<MemberRow, 'id'>, value: string) => {
    setDraftNewMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)),
    );
  };

  const handleSaveAdd = () => {
    const newErrors: Record<string, string> = {};
    draftNewMembers.forEach((member) => {
      const prefix = `draft-${member.id}`;
      if (!member.name) newErrors[`${prefix}-name`] = 'Name required';
      if (!member.role) newErrors[`${prefix}-role`] = 'Role required';
      if (!member.age) newErrors[`${prefix}-age`] = 'Age required';
      if (!member.gender) newErrors[`${prefix}-gender`] = 'Gender required';
    });
    setMemberErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      toast.error(newErrors[firstKey]);
      return;
    }
    const existing = data?.performing_members ?? [];
    const existingPayload = existing.map((m) => ({
      name: m.name,
      role: m.role,
      age: Number(m.age),
      gender_slug: typeof m.gender === 'string' ? m.gender : (m.gender as any)?.g_slug ?? '',
    }));
    const newPayload = draftNewMembers.map((m) => ({
      name: m.name,
      role: m.role,
      age: Number(m.age),
      gender_slug: m.gender,
    }));
    updatePerformanceDetails({
      performing_members: [...existingPayload, ...newPayload],
    })
      .then(() => {
        toast.success('Members added');
        setMembersViewMode('view');
        setDraftNewMembers([{ id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' }]);
      })
      .catch(() => { });
  };

  const handleCancelAdd = () => {
    setMembersViewMode('view');
    setDraftNewMembers([{ id: `draft-${Date.now()}`, name: '', role: '', age: '', gender: '' }]);
    setMemberErrors({});
  };

  const handleCancelPerformancePrefs = () => {
    if (!data) {
      setPerformancePrefsEditing(false);
      return;
    }
    if (data.performing_language?.length) {
      setLanguages(
        data.performing_language.map((l) =>
          canonicalPerformingLanguageSlug(l.l_slug),
        ),
      );
    } else {
      setLanguages([]);
    }
    setMinDuration(data.min_duration_mins != null ? String(data.min_duration_mins) : '');
    setMaxDuration(data.max_duration_mins != null ? String(data.max_duration_mins) : '');
    setOpenToTravel(normalizeOpenToTravel(data.open_to_travel as string | null | undefined));
    const crewNum = data.offstage_crew != null ? Number(data.offstage_crew) : null;
    setOffstageCrew(crewNum != null && crewNum >= 1 && crewNum <= 20 ? String(crewNum) : '');
    setPerformanceErrors({});
    setPerformancePrefsEditing(false);
  };

  const handleSavePerformanceBasics = () => {
    const newErrors: Record<string, string> = {};

    if (languages.length === 0) newErrors.languages = 'Select at least one performing language';
    if (!minDuration) newErrors.minDuration = 'Enter minimum duration';
    if (!maxDuration) newErrors.maxDuration = 'Enter maximum duration';
    if (!offstageCrew) newErrors.offstageCrew = 'Select offstage crew';

    const minVal = Number(minDuration);
    const maxVal = Number(maxDuration);

    if (!newErrors.minDuration) {
      if (Number.isNaN(minVal)) {
        newErrors.minDuration = 'Enter a valid minimum duration';
      } else if (minVal < 30) {
        newErrors.minDuration = 'Minimum duration must be at least 30 minutes';
      } else if (minVal > 480) {
        newErrors.minDuration = 'Minimum duration cannot be more than 480 minutes';
      }
    }

    if (!newErrors.maxDuration) {
      if (Number.isNaN(maxVal)) {
        newErrors.maxDuration = 'Enter a valid maximum duration';
      } else if (maxVal > 480) {
        newErrors.maxDuration = 'Maximum duration cannot be more than 480 minutes';
      } else if (maxVal < 30) {
        newErrors.maxDuration = 'Maximum duration must be at least 30 minutes';
      }
    }

    // Cross-field check: minimum must always be smaller than maximum
    if (!newErrors.minDuration && !newErrors.maxDuration && !Number.isNaN(minVal) && !Number.isNaN(maxVal)) {
      if (minVal >= maxVal) {
        newErrors.minDuration = 'Minimum duration must be smaller than maximum duration';
      }
    }

    setPerformanceErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      toast.error(newErrors[firstKey]);
      if (firstKey === 'languages') languagesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else performanceRefs.current[firstKey]?.focus();
      return;
    }

    const payload = {
      performing_language: languages.map((l) => l.toLowerCase()),
      min_duration_mins: Number(minDuration),
      max_duration_mins: Number(maxDuration),
      open_to_travel: openToTravel,
      offstage_crew: Number(offstageCrew),
    };

    updatePerformanceDetails(payload)
      .then(() => {
        setPerformancePrefsEditing(false);
      })
      .catch(() => {
        toast.error('Failed to save. Please try again.');
      });
  };

  const offstageCrewOptions = useMemo(
    () =>
      Array.from({ length: 20 }, (_, index) => {
        const value = String(index + 1);
        return { value, label: value };
      }),
    [],
  );

  return (
    <div className="w-full bg-[#0B0B0D] text-white">
      <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-16">
        {/* Header */}
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
              <Clock className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                Performance Details
              </h1>
              <p className="text-[#8E8E93] text-sm lg:text-base mt-1">
                Define how you perform and who performs with you
              </p>
            </div>
          </div>
        </header>

        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          className="space-y-6 lg:space-y-8 mb-16"
        >
          <section className="rounded-2xl space-y-5 p-5 lg:p-6 bg-[#0F0F11]/50 border border-[#3A3A3C]/30">
            {isLoading ? (
              <section className=" rounded-2xl p-5 lg:p-6 flex items-center justify-center min-h-[200px]">
                <Loader size="small" />
              </section>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <SlidersHorizontal className="h-5 w-5 text-[#8E8E93] shrink-0" />
                    <h2 className="text-sm lg:text-base font-semibold text-[#C7C7CC] uppercase tracking-wider">
                      Performance preferences
                    </h2>
                  </div>
                  {!performancePrefsEditing ? (
                    <div className="flex flex-shrink-0 justify-end sm:w-auto w-full">
                      <span className="relative inline-flex shrink-0">
                        <button
                          type="button"
                          onClick={() => setPerformancePrefsEditing(true)}
                          className="relative z-0 inline-flex items-center gap-1.5 rounded-full border border-[#3A3A3C] bg-[#151517] px-3 py-1.5 text-[11px] text-[#F2F2F7] text-nowrap transition-colors hover:bg-[#2C2C2E]"
                        >
                          Modify
                        </button>
                        {performancePrefsNeedAttention ? (
                          <ProfileIncompleteCornerPing className="right-0 top-0 z-[2] md:right-0 md:top-0" />
                        ) : null}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-end gap-2 w-full sm:w-auto flex-shrink-0">
                      <button
                        type="button"
                        onClick={handleCancelPerformancePrefs}
                        className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                      >
                        Cancel
                      </button>
                      <Button
                        type="button"
                        variant="active"
                        className="px-3 py-1.5 text-xs font-medium bg-white text-black hover:bg-white/90 hover:text-black disabled:opacity-50 disabled:pointer-events-none disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
                        onClick={handleSavePerformanceBasics}
                        disabled={!hasPerformancePrefsChanged || isLoading}
                        isLoading={saveLoading}
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </div>

                {!performancePrefsEditing ? (
                  <div className="pt-1">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      <div className="flex gap-3 rounded-xl border border-[#3A3A3C]/50 bg-[#151517]/50 p-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1C1C1E] border border-[#3A3A3C]/50">
                          <Clock className="h-4 w-4 text-[#8E8E93]" />
                        </div>
                        <div className="min-w-0 flex flex-col">
                          <p className="text-[10px] font-medium text-[#8E8E93] uppercase tracking-wider">Duration</p>
                          <p className="mt-0.5 text-sm font-medium text-[#E5E5EA] leading-snug">
                            {data?.min_duration_mins != null && data?.max_duration_mins != null
                              ? `${data.min_duration_mins} – ${data.max_duration_mins} mins`
                              : data?.min_duration_mins != null
                                ? `Min ${data.min_duration_mins} mins`
                                : data?.max_duration_mins != null
                                  ? `Up to ${data.max_duration_mins} mins`
                                  : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 rounded-xl border border-[#3A3A3C]/50 bg-[#151517]/50 p-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1C1C1E] border border-[#3A3A3C]/50">
                          <MapPin className="h-4 w-4 text-[#8E8E93]" />
                        </div>
                        <div className="min-w-0 flex flex-col">
                          <p className="text-[10px] font-medium text-[#8E8E93] uppercase tracking-wider">Open to travel</p>
                          <p className="mt-0.5 text-sm font-medium text-[#E5E5EA] leading-snug">
                            {savedOpenToTravel === 'nationwide' ? 'Nationwide' : savedOpenToTravel === 'no' ? 'No' : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 rounded-xl border border-[#3A3A3C]/50 bg-[#151517]/50 p-3 sm:col-span-2 lg:col-span-1">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1C1C1E] border border-[#3A3A3C]/50">
                          <Users className="h-4 w-4 text-[#8E8E93]" />
                        </div>
                        <div className="min-w-0 flex flex-col">
                          <p className="text-[10px] font-medium text-[#8E8E93] uppercase tracking-wider">Offstage crew</p>
                          <p className="mt-0.5 text-sm font-medium text-[#E5E5EA] leading-snug">
                            {data?.offstage_crew != null ? `${data.offstage_crew} ${Number(data.offstage_crew) === 1 ? 'person' : 'people'}` : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 rounded-xl border border-[#3A3A3C]/50 bg-[#151517]/50 p-3 sm:col-span-2 lg:col-span-1">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1C1C1E] border border-[#3A3A3C]/50">
                          <Languages className="h-4 w-4 text-[#8E8E93]" />
                        </div>
                        <div className="min-w-0 flex flex-col">
                          <p className="text-[10px] font-medium text-[#8E8E93] uppercase tracking-wider">Performing language</p>
                          <p className="mt-0.5 text-sm font-medium text-[#E5E5EA] leading-snug">
                            {data?.performing_language?.length
                              ? data.performing_language.map((l) => l.language_name).join(', ')
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div ref={languagesSectionRef} className="space-y-2">
                      <MultiSelect
                        label="Performing Language"
                        options={languageOptions}
                        value={languages}
                        onChange={setLanguages}
                        error={performanceErrors.languages}
                        hideErrorText
                        seeMoreCollapse={{
                          defaultPrimaryValues: [...DEFAULT_PERFORMING_LANGUAGE_PRIMARY],
                        }}
                      />
                    </div>

                    <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                      <Input
                        ref={(el) => { performanceRefs.current.minDuration = el; }}
                        label="Minimum duration"
                        value={minDuration}
                        onChange={(e) => {
                          const numeric = e.target.value.replace(/\D/g, '').slice(0, 3);
                          setMinDuration(numeric);
                        }}
                        inputMode="numeric"
                        placeholder="e.g. 30"
                        error={performanceErrors.minDuration}
                        hideErrorText
                      />
                      <Input
                        ref={(el) => { performanceRefs.current.maxDuration = el; }}
                        label="Maximum duration"
                        value={maxDuration}
                        onChange={(e) => {
                          const numeric = e.target.value.replace(/\D/g, '').slice(0, 3);
                          setMaxDuration(numeric);
                        }}
                        inputMode="numeric"
                        placeholder="e.g. 120"
                        error={performanceErrors.maxDuration}
                        hideErrorText
                      />
                      <Select
                        label="Open to travel"
                        options={[
                          { value: 'nationwide', label: 'Nationwide' },
                          { value: 'no', label: 'No' },
                        ]}
                        value={openToTravelValue}
                        onChange={setOpenToTravel}
                        placeholder="Select"
                      />
                      <Select
                        ref={(el) => { performanceRefs.current.offstageCrew = el; }}
                        label="Offstage crew"
                        options={offstageCrewOptions}
                        value={offstageCrew}
                        onChange={setOffstageCrew}
                        placeholder="Select"
                        error={performanceErrors.offstageCrew}
                        hideErrorText
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </section>


          <section className="rounded-2xl space-y-4 p-5 lg:p-6 bg-[#0F0F11]/50 border border-[#3A3A3C]/30">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-[#8E8E93]" />
                <h2 className="text-sm lg:text-base font-semibold text-[#C7C7CC] uppercase tracking-wider">
                  Performing members
                </h2>
              </div>
              {membersViewMode === 'view' && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAddMember}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Member
                  </button>
                  {(data?.performing_members?.length ?? 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => setMembersViewMode('modify')}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                    >
                      Modify
                    </button>
                  )}
                </div>
              )}
              {membersViewMode === 'modify' && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCancelModify}
                    className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                  >
                    Cancel
                  </button>
                  <Button
                    type="button"
                    variant="active"
                    className="px-3 py-1.5 text-xs font-medium bg-white text-black hover:bg-white/90 hover:text-black"
                    onClick={handleSaveModify}
                    disabled={isLoading}
                    isLoading={saveLoading}
                  >
                    Save
                  </Button>
                </div>
              )}
              {membersViewMode === 'add' && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCancelAdd}
                    className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors"
                  >
                    Cancel
                  </button>
                  <Button
                    type="button"
                    variant="active"
                    className="px-3 py-1.5 text-xs font-medium bg-white text-black hover:bg-white/90 hover:text-black"
                    onClick={handleSaveAdd}
                    disabled={saveLoading || isLoading}
                  >
                    {saveLoading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>

            {/* View / Modify: list of performing members */}
            {(membersViewMode === 'view' || membersViewMode === 'modify') && (
              <div className="rounded-xl border border-[#3A3A3C]/50 bg-[#0F0F11]/30 overflow-hidden">
                {(!data?.performing_members || data.performing_members.length === 0) ? (
                  <p className="text-sm text-[#6E6E73] py-8 px-4 text-center">
                    No performing members yet. Add one to get started.
                  </p>
                ) : (
                  <>
                    {/* Desktop: column headers */}
                    <div
                      className="hidden md:grid gap-3 px-4 py-2.5 sm:px-5 border-b border-[#3A3A3C]/40 text-[10px] font-semibold uppercase tracking-wider text-[#8E8E93]"
                      style={{ gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr) 70px 80px 44px' }}
                    >
                      <span>Name</span>
                      <span>Role</span>
                      <span>Age</span>
                      <span>Gender</span>
                      <span aria-hidden className="w-10" />
                    </div>
                    <ul className="divide-y divide-[#3A3A3C]/40">
                      {data.performing_members.map((member, index) => {
                        const apiId = member.id;
                        const genderLabel =
                          typeof member.gender === 'string'
                            ? member.gender
                            : (member.gender as { g_slug?: string })?.g_slug ?? '';
                        const genderDisplay =
                          genderOptions.find((g) => g.value === genderLabel)?.label ?? genderLabel;
                        const details = [
                          member.role || '—',
                          member.age != null ? `${member.age} yrs` : '—',
                          genderDisplay || '—',
                        ].filter(Boolean).join(' · ');
                        return (
                          <li
                            key={apiId ?? index}
                            className="flex flex-row md:grid gap-2 md:gap-3 px-4 py-3 sm:px-5 sm:py-3.5 hover:bg-[#151517]/50 transition-colors min-w-0 md:items-center"
                            style={{
                              gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr) 70px 80px 44px',
                            }}
                          >
                            {/* Col 1: avatar + name (details only on mobile) */}
                            <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-initial">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/20">
                                <UserCircle className="h-4 w-4 text-sky-500" />
                              </div>
                              <div className="min-w-0 flex flex-col">
                                <p className="text-sm font-medium text-white truncate">
                                  {member.name || '—'}
                                </p>
                                <p className="text-xs text-[#8E8E93] mt-0.5 truncate md:hidden">
                                  {details}
                                </p>
                              </div>
                            </div>
                            {/* Desktop only: role, age, gender columns */}
                            <p className="hidden md:block text-sm text-[#E5E5EA] truncate min-w-0" data-column="role">
                              {member.role || '—'}
                            </p>
                            <p className="hidden md:block text-sm text-[#E5E5EA] tabular-nums" data-column="age">
                              {member.age != null ? `${member.age} yrs` : '—'}
                            </p>
                            <p className="hidden md:block text-sm text-[#E5E5EA] truncate min-w-0" data-column="gender">
                              {genderDisplay || '—'}
                            </p>
                            {/* Action */}
                            <div className="flex shrink-0 justify-end md:justify-center md:col-span-1">
                              {membersViewMode === 'modify' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (apiId == null) return;
                                    if ((data?.performing_members?.length ?? 0) <= 1) {
                                      toast.error('At least one performing member is required.');
                                      return;
                                    }
                                    handleDeletePerformingMember(apiId);
                                  }}
                                  className="inline-flex items-center justify-center rounded-full p-2 text-[#8E8E93] hover:text-[#F2F2F7] hover:bg-[#2C2C2E] border border-[#3A3A3C]/50 transition-colors"
                                  aria-label="Remove member"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}

            {/* Add mode: list-style form (same structure as view list) */}
            {membersViewMode === 'add' && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={handleAddMoreMembers}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add more members
                </button>
                <div className="rounded-xl border border-[#3A3A3C]/50 bg-[#0F0F11]/30 overflow-hidden">
                  {/* Desktop: column headers (match view list) */}
                  <div
                    className="hidden md:grid gap-3 px-4 py-2.5 sm:px-5 border-b border-[#3A3A3C]/40 text-[10px] font-semibold uppercase tracking-wider text-[#8E8E93]"
                    style={{ gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr) 70px 80px 44px' }}
                  >
                    <span>Name</span>
                    <span>Role</span>
                    <span>Age</span>
                    <span>Gender</span>
                    <span aria-hidden className="w-10" />
                  </div>
                  <div className="divide-y divide-[#3A3A3C]/40">
                    {draftNewMembers.map((member) => {
                      const prefix = `draft-${member.id}`;
                      return (
                        <div
                          key={member.id}
                          className="flex flex-col md:grid gap-3 px-4 py-3 sm:px-5 sm:py-3.5 min-w-0 md:items-center"
                          style={{
                            gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr) 70px 80px 44px',
                          }}
                        >
                          {/* Col 1: avatar + Name input (same as view list) */}
                          <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-initial">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/20">
                              <UserCircle className="h-4 w-4 text-sky-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <Input
                                ref={(el) => { memberRefs.current[`${prefix}-name`] = el; }}
                                placeholder="Name"
                                aria-label="Name"
                                value={member.name}
                                onChange={(e) => handleDraftMemberChange(member.id, 'name', e.target.value)}
                                error={memberErrors[`${prefix}-name`]}
                                hideErrorText
                                className="h-10 text-sm"
                              />
                            </div>
                          </div>
                          {/* Col 2: Role */}
                          <div className="min-w-0">
                            <Input
                              ref={(el) => { memberRefs.current[`${prefix}-role`] = el; }}
                              placeholder="Role"
                              aria-label="Role"
                              value={member.role}
                              onChange={(e) => handleDraftMemberChange(member.id, 'role', e.target.value)}
                              error={memberErrors[`${prefix}-role`]}
                              hideErrorText
                              className="h-10 text-sm"
                            />
                          </div>
                          {/* Col 3: Age */}
                          <div className="min-w-0">
                            <Input
                              ref={(el) => { memberRefs.current[`${prefix}-age`] = el; }}
                              placeholder="Age"
                              aria-label="Age"
                              value={member.age}
                              onChange={(e) =>
                                handleDraftMemberChange(
                                  member.id,
                                  'age',
                                  e.target.value.replace(/\D/g, '').slice(0, 2),
                                )
                              }
                              inputMode="numeric"
                              error={memberErrors[`${prefix}-age`]}
                              hideErrorText
                              className="h-10 text-sm"
                            />
                          </div>
                          {/* Col 4: Gender */}
                          <div className="min-w-0 [&_button]:h-10 [&_button]:py-2 [&_button]:text-sm">
                            <Select
                              ref={(el) => { memberRefs.current[`${prefix}-gender`] = el; }}
                              options={genderOptions}
                              value={member.gender}
                              onChange={(v) => handleDraftMemberChange(member.id, 'gender', v)}
                              placeholder="Gender"
                              error={memberErrors[`${prefix}-gender`]}
                              hideErrorText
                            />
                          </div>
                          {/* Col 5: Remove */}
                          <div className="flex shrink-0 justify-end md:justify-center">
                            {draftNewMembers.length > 1 ? (
                              <button
                                type="button"
                                onClick={() => handleRemoveDraftMember(member.id)}
                                className="inline-flex items-center justify-center rounded-full p-2 text-[#8E8E93] hover:text-[#F2F2F7] hover:bg-[#2C2C2E] border border-[#3A3A3C]/50 transition-colors"
                                aria-label="Remove member"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </section>
        </motion.form>
      </div>
    </div>
  );
};

